import React, {useEffect, useMemo, useState} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Chip,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Stack
} from '@mui/material';
import Popover from '@mui/material/Popover';
import PopupState, {bindPopover, bindTrigger} from 'material-ui-popup-state';
import DownloadIcon from '@mui/icons-material/Download';
import InfoIcon from '@mui/icons-material/Info';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import bibliographyUrl from './meta/bibliography.bib';

const SECTION_LABELS = {
    article: 'Journal articles',
    inproceedings: 'Conference papers',
    incollection: 'Book chapters',
    book: 'Books',
    techreport: 'Technical reports',
};

const SECTION_ORDER = ['book', 'incollection', 'article', 'inproceedings'];
const SECTION_COLORS = ['#eef4ff', '#effaf2', '#fff8eb', '#f5f0ff', '#eefbfb', '#f7f7f7'];

const LATEX_SPECIAL_REPLACEMENTS = {
    '\\\\&': '&',
    '\\\\%': '%',
    '\\\\_': '_',
    '\\\\#': '#',
    '\\\\$': '$',
    '\\\\textasciitilde': '~',
    '\\\\textasciicircum': '^',
    '\\\\ae': 'ae',
    '\\\\AE': 'AE',
    '\\\\oe': 'oe',
    '\\\\OE': 'OE',
    '\\\\aa': 'aa',
    '\\\\AA': 'AA',
    '\\\\o': 'o',
    '\\\\O': 'O',
    '\\\\ss': 'ss',
    '\\\\l': 'l',
    '\\\\L': 'L',
    '\\\\i': 'i',
    '\\\\j': 'j',
};

const LATEX_COMBINING_ACCENTS = {
    '`': '\u0300',
    "'": '\u0301',
    '^': '\u0302',
    '"': '\u0308',
    '~': '\u0303',
    '=': '\u0304',
    '.': '\u0307',
    u: '\u0306',
    v: '\u030C',
    c: '\u0327',
    H: '\u030B',
};

function readBracedValue(text, startIndex) {
    let depth = 0;
    let value = '';

    for (let index = startIndex; index < text.length; index += 1) {
        const char = text[index];
        if (char === '{') {
            depth += 1;
            if (depth > 1) {
                value += char;
            }
            continue;
        }
        if (char === '}') {
            depth -= 1;
            if (depth === 0) {
                return {value, endIndex: index + 1};
            }
            value += char;
            continue;
        }
        value += char;
    }

    return {value, endIndex: text.length};
}

function readQuotedValue(text, startIndex) {
    let value = '';

    for (let index = startIndex + 1; index < text.length; index += 1) {
        const char = text[index];
        if (char === '"' && text[index - 1] !== '\\') {
            return {value, endIndex: index + 1};
        }
        value += char;
    }

    return {value, endIndex: text.length};
}

function normalizeValue(value) {
    let normalized = value;

    Object.entries(LATEX_SPECIAL_REPLACEMENTS).forEach(([latex, replacement]) => {
        normalized = normalized.replaceAll(latex, replacement);
    });

    normalized = normalized.replace(/\\([`'"^~=.uvcH])\s*\{?\s*([A-Za-z])\s*\}?/g, (_match, accent, letter) => {
        const combining = LATEX_COMBINING_ACCENTS[accent];
        if (!combining) {
            return letter;
        }
        return `${letter}${combining}`.normalize('NFC');
    });

    return normalized
        .replace(/[{}]/g, '')
        .replace(/\\\s+/g, ' ')
        .replace(/\\(?!\\)/g, '')
        .replace(/\s+/g, ' ')
        .trim();
}

function getSectionSortRank(type) {
    const sectionIndex = SECTION_ORDER.indexOf(type);
    return sectionIndex === -1 ? Number.MAX_SAFE_INTEGER : sectionIndex;
}

function getSectionTitleWithCount(section) {
    return `${section.label} (${section.items.length})`;
}


function parseBibEntry(entryText) {
    const headerMatch = entryText.match(/^@(\w+)\s*{\s*([^,]+)\s*,/i);
    if (!headerMatch) {
        return null;
    }

    const type = headerMatch[1].toLowerCase();
    const id = headerMatch[2].trim();
    const fields = {};

    let cursor = headerMatch[0].length;
    const content = entryText.slice(0, -1);

    while (cursor < content.length) {
        while (cursor < content.length && /[\s,]/.test(content[cursor])) {
            cursor += 1;
        }
        if (cursor >= content.length) {
            break;
        }

        const fieldStart = cursor;
        while (cursor < content.length && /[\w-]/.test(content[cursor])) {
            cursor += 1;
        }
        const field = content.slice(fieldStart, cursor).toLowerCase();

        while (cursor < content.length && /\s/.test(content[cursor])) {
            cursor += 1;
        }
        if (content[cursor] !== '=') {
            while (cursor < content.length && content[cursor] !== ',') {
                cursor += 1;
            }
            continue;
        }
        cursor += 1;

        while (cursor < content.length && /\s/.test(content[cursor])) {
            cursor += 1;
        }

        let parsedValue = {value: '', endIndex: cursor};
        if (content[cursor] === '{') {
            parsedValue = readBracedValue(content, cursor);
        } else if (content[cursor] === '"') {
            parsedValue = readQuotedValue(content, cursor);
        } else {
            const valueStart = cursor;
            while (cursor < content.length && content[cursor] !== ',') {
                cursor += 1;
            }
            parsedValue = {value: content.slice(valueStart, cursor), endIndex: cursor};
        }

        fields[field] = normalizeValue(parsedValue.value);
        cursor = parsedValue.endIndex;
    }

    const authors = (fields.author || '')
        .split(/\s+and\s+/i)
        .map((name) => name.trim())
        .filter(Boolean);

    const year = Number.parseInt(fields.year, 10);

    return {
        id,
        type,
        title: fields.title || id,
        venue: fields.journal || fields.booktitle || fields.institution || fields.publisher || '',
        booktitle: fields.booktitle || '',
        year: Number.isNaN(year) ? null : year,
        abstract: fields.abstract || '',
        authors,
        url: fields.url || (fields.doi ? `https://doi.org/${fields.doi}` : ''),
    };
}

function parseBibTeX(text) {
    const entries = [];
    let cursor = 0;

    while (cursor < text.length) {
        const atIndex = text.indexOf('@', cursor);
        if (atIndex === -1) {
            break;
        }

        const firstBrace = text.indexOf('{', atIndex);
        if (firstBrace === -1) {
            break;
        }

        let depth = 0;
        let endIndex = firstBrace;

        for (; endIndex < text.length; endIndex += 1) {
            const char = text[endIndex];
            if (char === '{') {
                depth += 1;
            } else if (char === '}') {
                depth -= 1;
                if (depth === 0) {
                    endIndex += 1;
                    break;
                }
            }
        }

        const rawEntry = text.slice(atIndex, endIndex).trim();
        const parsedEntry = parseBibEntry(rawEntry);
        if (parsedEntry) {
            entries.push(parsedEntry);
        }

        cursor = endIndex;
    }

    return entries;
}

function groupByType(publications) {
    const grouped = publications.reduce((accumulator, publication) => {
        const typeKey = SECTION_LABELS[publication.type] ? publication.type : 'other';
        if (!accumulator[typeKey]) {
            accumulator[typeKey] = [];
        }
        accumulator[typeKey].push(publication);
        return accumulator;
    }, {});

    return Object.keys(grouped)
        .sort((leftType, rightType) => {
            const leftRank = getSectionSortRank(leftType);
            const rightRank = getSectionSortRank(rightType);
            if (leftRank !== rightRank) {
                return leftRank - rightRank;
            }
            return (SECTION_LABELS[leftType] || leftType).localeCompare(SECTION_LABELS[rightType] || rightType);
        })
        .map((type) => ({
            type,
            label: SECTION_LABELS[type] || 'Other publications',
            items: grouped[type].sort((left, right) => {
            const leftYear = left.year ?? 0;
            const rightYear = right.year ?? 0;
            if (leftYear !== rightYear) {
                return rightYear - leftYear;
            }
            return left.title.localeCompare(right.title);
        }),
        }));
}

export default function Publis() {
    const [publications, setPublications] = useState([]);
    const [error, setError] = useState('');

    useEffect(() => {
        let isMounted = true;

        fetch(bibliographyUrl)
            .then((response) => {
                if (!response.ok) {
                    throw new Error('Unable to load bibliography file.');
                }
                return response.text();
            })
            .then((content) => {
                if (!isMounted) {
                    return;
                }
                setPublications(parseBibTeX(content));
            })
            .catch((loadError) => {
                if (!isMounted) {
                    return;
                }
                setError(loadError.message || 'Unable to load bibliography file.');
            });

        return () => {
            isMounted = false;
        };
    }, []);

    const publicationSections = useMemo(() => groupByType(publications), [publications]);

    if (error) {
        return <Typography color="error">{error}</Typography>;
    }

    return (
        <Box sx={{width: '100%', height: '100%', display: 'flex', flexDirection: 'column', gap: 2}}>
            <Box sx={{position: 'sticky', top: 8, zIndex: 2, py: 1, backgroundColor: 'background.paper'}}>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {publicationSections.map((section, sectionIndex) => (
                        <Chip
                            key={section.type}
                            label={(
                                <Box component="span" sx={{display: 'inline-flex', alignItems: 'center', gap: 0.75}}>
                                    <Box
                                        component="span"
                                        sx={{
                                            width: 9,
                                            height: 9,
                                            borderRadius: '50%',
                                            backgroundColor: SECTION_COLORS[sectionIndex % SECTION_COLORS.length],
                                            border: '1px solid rgba(0,0,0,0.18)'
                                        }}
                                    />
                                    {getSectionTitleWithCount(section)}
                                </Box>
                            )}
                            component="a"
                            clickable
                            href={`#section-${section.type}`}
                            variant="outlined"
                            sx={{
                                textDecoration: 'none',
                                borderColor: '#d8dde8',
                                backgroundColor: '#ffffffcc'
                            }}
                        />
                    ))}
                </Stack>
            </Box>

            {publicationSections.map((section, sectionIndex) => (
                <Accordion
                    key={section.type}
                    id={`section-${section.type}`}
                    defaultExpanded={section.type !== 'techreport'}
                    disableGutters
                    elevation={0}
                    sx={{
                        border: '1px solid #ededed',
                        borderRadius: 1,
                        scrollMarginTop: 72,
                        backgroundColor: SECTION_COLORS[sectionIndex % SECTION_COLORS.length],
                        '&:before': {display: 'none'}
                    }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                        <Typography variant="h6">{getSectionTitleWithCount(section)}</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{pt: 0}}>
                        <List>
                            {section.items.map((publication, index) => (
                                <ListItem key={publication.id} alignItems="flex-start">
                                    <ListItemAvatar sx={{minWidth: 88, mt: 0}}>
                                        <Box sx={{display: 'flex', gap: 0.5, alignItems: 'center', justifyContent: 'center'}} component="span">
                                            <PopupState variant="popover" popupId={`${section.type}-abstract-${index}`}>
                                                {(popupState) => (
                                                    <div>
                                                        <IconButton
                                                            disabled={!publication.abstract}
                                                            variant="outlined"
                                                            size="small"
                                                            sx={{width: 34, height: 34}}
                                                            {...bindTrigger(popupState)}
                                                        >
                                                            <InfoIcon fontSize="small"/>
                                                        </IconButton>

                                                        <Popover
                                                            {...bindPopover(popupState)}
                                                            anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                                                            transformOrigin={{vertical: 'top', horizontal: 'center'}}
                                                        >
                                                            <Typography sx={{p: 2, maxWidth: 500}}>{publication.abstract}</Typography>
                                                        </Popover>
                                                    </div>
                                                )}
                                            </PopupState>

                                            <IconButton
                                                disabled={!publication.url}
                                                variant="contained"
                                                size="small"
                                                sx={{width: 34, height: 34}}
                                                component="a"
                                                href={publication.url || undefined}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                <DownloadIcon fontSize="small"/>
                                            </IconButton>
                                        </Box>
                                    </ListItemAvatar>

                                    <ListItemText
                                        primary={
                                            <Box component="span" sx={{display: 'flex', flexDirection: 'column'}}>
                                                <Box component="span" sx={{display: 'flex'}}>
                                                    <Typography component="span" variant="body2" sx={{color: 'text.primary', display: 'inline'}}>
                                                        {publication.title}
                                                    </Typography>
                                                    {publication.year && (
                                                        <Typography component="span">&nbsp;({publication.year})</Typography>
                                                    )}
                                                </Box>

                                                {publication.booktitle ? (
                                                    <Typography component="span" variant="caption" sx={{color: 'text.secondary'}}>
                                                        {publication.booktitle}
                                                    </Typography>
                                                ) : publication.venue ? (
                                                    <Typography component="span" variant="caption" sx={{color: 'text.secondary'}}>
                                                        {publication.venue}
                                                    </Typography>
                                                ) : null}
                                            </Box>
                                        }
                                        secondary={
                                            <Typography component="span" variant="body2">
                                                {publication.authors.join(', ')}
                                            </Typography>
                                        }
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </AccordionDetails>
                </Accordion>
            ))}
        </Box>
    );
}
